import {isDefinedAndPopulated} from './typeGuards';

import type {LinkField} from 'types/payload-types';

/**
 * Returns the appropriate `href` value for a given link object based on its `linkType`.
 * This utility function evaluates the type of link and builds the correct `href` string accordingly.
 * It supports link types such as phone (`tel:`), email (`mailto:` with subject and body), external URLs, internal pages,
 * anchor links within pages, and file downloads. If no valid destination is found, a fallback such as `'/'` or `'#'` is returned.
 * This function ensures consistent URL generation for all link types defined in the `LinkField` schema.
 *
 * @param link The link object containing type and related metadata to resolve the target URL.
 * @returns A string representing the resolved `href` value for use in anchors or buttons.
 *
 * @example
 * ```tsx
 * const href = getHrefForLinkType(link);
 * return <a href={href}>{link.label}</a>;
 * ```
 */
export const getHrefForLinkType = (link: LinkField) => {
    if (link.linkType === 'phone') {
        return `tel: ${link.phone}`;
    }
    if (link.linkType === 'email') {
        return `mailto: ${link.email}?subject=${link.emailSubject ?? ''}&body=${link.emailBody ?? ''}`;
    }
    if (link.linkType === 'external') {
        return link.externalUrl ?? '';
    }
    if (link.linkType === 'internal') {
        if (isDefinedAndPopulated(link.internalLink)) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            return link.internalLink.slug ?? '/';
        }

        return '/';
    }
    if (link.linkType === 'anchor') {
        if (isDefinedAndPopulated(link.internalBlockLink)) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            return `${link.internalBlockLink?.slug ?? ''}${link?.blockId ?? ''}`;
        }

        return '/';
    }

    if (isDefinedAndPopulated(link.file)) {
        return link.file.url ?? '';
    }

    return '#';
};

/**
 * Determines the appropriate value for the `download` attribute of an anchor tag based on the link configuration.
 * This function checks if the link type is `'file'` and whether the `directDownload` option is enabled.
 * If so, it returns either a custom filename (if provided) or `true` to trigger a default download.
 * Otherwise, it returns `false`, indicating that the `download` attribute should not be set.
 *
 * @param link The link object containing type, download settings, and optional filename.
 * @returns A string with the custom filename, `true` for default behavior, or `false` if downloading is not applicable.
 *
 * @example
 * ```tsx
 * const download = getDownloadAttribute(link);
 * <a href={href} download={download}>{link.label}</a>;
 * ```
 */
export const getDownloadAttribute = (link: LinkField) => {
    if (link.linkType === 'file' && link.directDownload) {
        return link.downloadFilename ?? true;
    }

    return false;
};

/**
 * Generates the `rel` attribute string for an anchor tag based on the given external link configuration.
 * This function ensures that security and SEO best practices are followed when rendering external links.
 * For external links, it always includes `"noopener noreferrer"` to prevent tab-napping vulnerabilities.
 * If the `rel` checkbox is **not** checked (meaning "do not follow"), it appends `"nofollow"` to discourage search engine indexing.
 * For all non-external link types, it returns `undefined`, allowing the anchor to omit the `rel` attribute entirely.
 *
 * @param link The link object from which to determine the appropriate `rel` attribute.
 * @returns A string containing the `rel` attribute value, or `undefined` if not applicable.
 *
 * @example
 * ```tsx
 * const rel = getRelAttributes(link);
 * <a href={href} rel={rel}>{link.label}</a>;
 * ```
 */
export const getRelAttributes = (link: LinkField) => {
    if (link.linkType === 'external') {
        return `noopener noreferrer${link.rel ? '' : ' nofollow'}` as const;
    }

    return undefined;
};

/**
 * Determines the appropriate `target` attribute value for a given link based on its type.
 * This utility inspects the `linkType` property of the provided `link` object. If the link is of type
 * `'external'`, it returns `'_blank'`, which instructs the browser to open the link in a new tab or window.
 * For all other link types, it returns `undefined`, meaning the link will follow the browser's default
 * behavior (typically opening in the same tab).
 *
 * @param link The link object containing information about its type and other metadata.
 * @returns `'_blank'` if the link is external, otherwise `undefined`.
 *
 * @example
 * ```tsx
 * const target = getTargetAttribute({ linkType: 'external', url: 'https://example.com' });
 * // target === '_blank'
 * ```
 */
export const getTargetAttribute = (link: LinkField) => {
    if (link.linkType === 'external') {
        return '_blank' as const;
    }

    return undefined;
};

/**
 * Extracts and normalizes the necessary data for rendering a link element from a `LinkField`.
 * This utility function processes a `LinkField` object and returns the resolved properties
 * required for rendering a valid `<a>` tag. It determines the `href` via `getHrefForLinkType`,
 * checks whether the link should trigger a file download, and assigns appropriate `rel`
 * and `target` attributes depending on the link type.
 * External and file links are automatically assigned `target="_blank"` to open in a new tab,
 * while other link types inherit default browser behavior.
 *
 * @param link The link configuration object containing metadata such as label, type, and target.
 * @returns An object containing the normalized link properties: `href`, `isDownload`, `label`, `rel`, and `target`.
 *
 * @example
 * ```tsx
 * const { href, target, label } = getLinkData({
 *   label: 'Documentation',
 *   linkType: 'external',
 *   url: 'https://example.com/docs'
 * });
 *
 * // href: 'https://example.com/docs'
 * // target: '_blank'
 * // label: 'Documentation'
 * ```
 */
export const getLinkData = (link: LinkField | undefined) => {
    if (!link) return {};

    const {label, linkType} = link;
    const href = getHrefForLinkType(link);
    const isDownload = getDownloadAttribute(link);
    const rel = getRelAttributes(link);
    const target = linkType === 'external' || linkType === 'file' ? '_blank' as const : undefined;

    return {
        href,
        isDownload,
        label,
        rel,
        target
    };
};